import manifest from '../../games.json';
import './styles.css';

interface GameEntry {
  slug: string;
  title: string;
  description: string;
  publicUrl?: string;
}

interface GamesManifest {
  games: GameEntry[];
}

const games = (manifest as GamesManifest).games;
const root = document.querySelector<HTMLDivElement>('#root');

if (!root) {
  throw new Error('Launcher root element is missing.');
}

const launcherRoot = root;

function getConfiguredBaseUrl(): string | null {
  const value = import.meta.env.VITE_GAME_BASE_URL?.trim();
  return value ? value.replace(/\/+$/, '') : null;
}

function getGameHref(game: GameEntry, baseUrl: string | null): string {
  if (game.publicUrl) {
    return game.publicUrl;
  }
  if (baseUrl) {
    return `${baseUrl}/${encodeURIComponent(game.slug)}/`;
  }
  return `./${game.slug}/`;
}

function createElement<K extends keyof HTMLElementTagNameMap>(
  tagName: K,
  className?: string,
): HTMLElementTagNameMap[K] {
  const element = document.createElement(tagName);
  if (className) {
    element.className = className;
  }
  return element;
}

function createBrandMark(): HTMLElement {
  const mark = createElement('span', 'brand-mark');
  mark.setAttribute('aria-hidden', 'true');
  for (let index = 0; index < 3; index += 1) {
    mark.append(createElement('span', `brand-mark-bar bar-${index + 1}`));
  }
  return mark;
}

function createGameCard(game: GameEntry, index: number, baseUrl: string | null): HTMLAnchorElement {
  const card = createElement('a', 'game-card');
  card.href = getGameHref(game, baseUrl);
  card.dataset.gameSlug = game.slug;
  card.setAttribute('aria-label', `Play ${game.title}`);

  const top = createElement('div', 'game-card-top');
  const number = createElement('span', 'game-number');
  number.textContent = String(index + 1).padStart(2, '0');
  const arrow = createElement('span', 'game-arrow');
  arrow.setAttribute('aria-hidden', 'true');
  arrow.textContent = '↗';
  top.append(number, arrow);

  const copy = createElement('div', 'game-card-copy');
  const title = createElement('h3');
  title.textContent = game.title;
  const description = createElement('p');
  description.textContent = game.description;
  copy.append(title, description);

  const action = createElement('span', 'game-card-action');
  action.textContent = 'Play now';
  card.append(top, copy, action);
  return card;
}

function renderLauncher(): void {
  const shell = createElement('div', 'site-shell');

  const header = createElement('header', 'site-header');
  const brand = createElement('a', 'brand');
  brand.href = './';
  brand.append(createBrandMark());
  const brandText = createElement('span', 'brand-text');
  const brandKicker = createElement('span', 'brand-kicker');
  brandKicker.textContent = 'H5GAMES';
  const brandSubtitle = createElement('span', 'brand-subtitle');
  brandSubtitle.textContent = 'Small browser games';
  brandText.append(brandKicker, brandSubtitle);
  brand.append(brandText);
  header.append(brand);

  const intro = createElement('section', 'intro');
  const eyebrow = createElement('p', 'eyebrow');
  eyebrow.textContent = 'A QUIET PLACE TO PLAY';
  const heading = createElement('h1');
  heading.textContent = 'Make room for play.';
  const lead = createElement('p', 'intro-copy');
  lead.textContent = 'Small, thoughtful games for a few minutes of focus, whenever you need them.';
  intro.append(eyebrow, heading, lead);

  const library = createElement('section', 'library');
  library.setAttribute('aria-labelledby', 'library-title');
  const libraryHeading = createElement('div', 'library-heading');
  const libraryTitle = createElement('h2');
  libraryTitle.id = 'library-title';
  libraryTitle.textContent = 'Game library';
  const gameCount = createElement('span', 'game-count');
  gameCount.textContent = `${games.length} ${games.length === 1 ? 'game' : 'games'}`;
  libraryHeading.append(libraryTitle, gameCount);

  const grid = createElement('div', 'game-grid');
  const baseUrl = getConfiguredBaseUrl();
  if (games.length === 0) {
    const emptyState = createElement('p', 'empty-state');
    emptyState.textContent = 'New games are on their way.';
    grid.append(emptyState);
  } else {
    games.forEach((game, index) => grid.append(createGameCard(game, index, baseUrl)));
  }
  library.append(libraryHeading, grid);

  const footer = createElement('footer', 'site-footer');
  footer.textContent = 'Runs in your browser · No sign-in required';

  shell.append(header, intro, library, footer);
  launcherRoot.replaceChildren(shell);
}

renderLauncher();
